import React, { useState } from "react";
import { Download, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

/**
 * Phase 24e · Download button (buyer). Issues a signed 24h JWT download URL
 * (max 3 downloads/transaction) and triggers the file transfer via anchor tag.
 */
export function AssetDownloadButton({ transactionId, downloadCount = 0, maxDownloads = 3 }) {
  const [loading, setLoading] = useState(false);
  const left = Math.max(0, maxDownloads - downloadCount);
  const disabled = loading || left <= 0;

  async function handleDownload() {
    setLoading(true);
    try {
      const res = await apiFetch(`/marketplace/purchases/${transactionId}/issue-download`, {
        method: "POST",
      });
      const token = res?.token;
      if (!token) throw new Error("no_token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${backend}/api/marketplace/download?token=${encodeURIComponent(token)}`;
      // Fetch bytes via credentials-include, then trigger save
      const dlRes = await fetch(url, { credentials: "include" });
      if (!dlRes.ok) {
        const j = await dlRes.json().catch(() => ({}));
        throw new Error(j?.detail?.error || "download_failed");
      }
      const blob = await dlRes.blob();
      const cd = dlRes.headers.get("Content-Disposition") || "";
      const nameMatch = /filename="?([^"]+)"?/i.exec(cd);
      const filename = nameMatch ? nameMatch[1] : "asset.bin";
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success("Download avviato");
    } catch (e) {
      toast.error(`Download fallito: ${String(e?.message || e)}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      data-testid="asset-download-btn"
      onClick={handleDownload}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold uppercase tracking-widest transition-all ${
        disabled
          ? "border-white/10 bg-white/5 text-text-secondary cursor-not-allowed"
          : "border-[var(--color-primary)]/60 bg-white hover:bg-[#EDEDED] text-black hover:shadow-lg hover:shadow-[var(--color-primary)]/30"
      }`}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : left <= 0 ? (
        <AlertCircle className="h-4 w-4" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      {left <= 0 ? "Limite download raggiunto" : `Scarica (${left}/${maxDownloads} rimasti)`}
    </button>
  );
}

export default AssetDownloadButton;
